/**
 * ExternalAccessPanel Component (REQ-AUTH-079)
 *
 * The portal's External access section: the outside accounts — existing ZITADEL
 * accounts owned by other organizations — that hold a platform grant belonging
 * to the selected organization.
 *
 * This is deliberately a SEPARATE list from the members roster rather than rows
 * merged into it. A member of the organization and an outsider holding a grant
 * are not the same status, and the two sets come from two different ZITADEL
 * queries whose union cannot be paginated.
 *
 * Each grant carries a role, Admin or Member, as a member does. Admin is the
 * grant dialog's default: the usual outside account is our own staff, there to
 * configure the customer's workspace (MCP connections and the like). A role
 * change is confirmed first, as it is on the members roster.
 *
 * D6 governs what hides: the TABLE renders only when there is something in it,
 * while the grant action stays available to staff. A section that appeared only
 * once it was non-empty would have no way of becoming non-empty.
 *
 * @module components/ExternalAccessPanel
 */

import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import RemoveModeratorOutlinedIcon from '@mui/icons-material/RemoveModeratorOutlined';
import type { ExternalAccessGrant, UserRole } from '../types';
import { CopyableId } from './CopyableId';

export interface ExternalAccessPanelProps {
  /** The outside accounts holding access. */
  items: ExternalAccessGrant[];
  /** The backend scanned only part of the organization's authorizations. */
  truncated: boolean;
  /** Whether this caller may grant, revoke and change roles — staff only (D1). */
  canManage: boolean;
  isLoading: boolean;
  error: Error | null;
  onGrant: (userId: string, role: UserRole) => Promise<void>;
  onRevoke: (userId: string) => Promise<void>;
  onSetRole: (userId: string, role: UserRole) => Promise<void>;
}

const formatRole = (role: UserRole) => (role === 'admin' ? 'Admin' : 'Member');

export function ExternalAccessPanel({
  items,
  truncated,
  canManage,
  isLoading,
  error,
  onGrant,
  onRevoke,
  onSetRole,
}: ExternalAccessPanelProps) {
  const [grantOpen, setGrantOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<UserRole>('admin');
  // The refusal of the grant asked for in the dialog. Kept apart from the
  // section's `error`, which also carries load, revoke and role-change failures
  // that are not this grant's refusal.
  const [grantError, setGrantError] = useState<string | null>(null);
  // The row whose role change awaits confirmation (null = dialog closed).
  const [roleTarget, setRoleTarget] = useState<ExternalAccessGrant | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Every dialog starts fresh: no id, Admin, no refusal. A cancelled choice is
  // not carried into the next grant.
  const openGrant = useCallback(() => {
    setUserId('');
    setRole('admin');
    setGrantError(null);
    setGrantOpen(true);
  }, []);

  // Only a grant the SERVICE accepted closes the dialog. A refusal — already a
  // member, already granted, no such account — keeps it open with the id, so
  // the refusal is read beside the id it was about and the operator can correct
  // it instead of pasting it again.
  const handleGrant = useCallback(async () => {
    const trimmed = userId.trim();
    if (!trimmed) {
      return; // there is no sensible default for "which account"
    }
    setBusy(true);
    setGrantError(null);
    try {
      await onGrant(trimmed, role);
      setGrantOpen(false);
    } catch (err) {
      setGrantError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [userId, role, onGrant]);

  const handleRevoke = useCallback(
    async (id: string) => {
      setBusy(true);
      try {
        await onRevoke(id);
      } catch {
        // Same: shown on the section's alert, and the row stays as the service
        // still reports it.
      } finally {
        setBusy(false);
      }
    },
    [onRevoke],
  );

  const requestRoleChange = useCallback((item: ExternalAccessGrant) => {
    setRoleError(null);
    setRoleTarget(item);
  }, []);

  // A refusal keeps the dialog open with the reason, so the operator can retry
  // or cancel; the row keeps the role the service still reports.
  const handleSetRole = useCallback(async () => {
    if (!roleTarget) {
      return;
    }
    setBusy(true);
    setRoleError(null);
    try {
      await onSetRole(
        roleTarget.userId,
        roleTarget.role === 'admin' ? 'member' : 'admin',
      );
      setRoleTarget(null);
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [roleTarget, onSetRole]);

  const promoting = roleTarget?.role !== 'admin';

  const hasRows = items.length > 0;

  // Nothing to show and nothing to do: render no section at all rather than an
  // empty heading a reader has to interpret.
  if (!hasRows && !canManage && !error) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }} data-testid="external-access-panel">
      <Stack
        direction="row"
        sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box>
          <Typography variant="h6" component="h2">
            External access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Accounts from other organizations that can reach this one. They are
            not members: they do not appear on the members list and use no seat.
          </Typography>
        </Box>
        {canManage && (
          <Button
            variant="outlined"
            onClick={openGrant}
            disabled={isLoading}
          >
            Grant external access
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {truncated && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="external-access-truncated"
        >
          This organization has more grants than we list here, so this is part of
          the picture rather than all of it.
        </Alert>
      )}

      {/* D6: the table exists only when it has rows. */}
      {hasRows && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="External access">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Login name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>User id</TableCell>
                <TableCell>Home organization</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const isAdmin = item.role === 'admin';
                return (
                  <TableRow key={item.userId}>
                    <TableCell>{item.displayName}</TableCell>
                    <TableCell>{item.loginName}</TableCell>
                    <TableCell>{formatRole(item.role)}</TableCell>
                    <TableCell>
                      <CopyableId
                        value={item.userId}
                        testIdPrefix="copy-external-user-id"
                      />
                    </TableCell>
                    <TableCell>{item.homeOrgId}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip
                          title={isAdmin ? 'Change to member' : 'Make admin'}
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label={
                                isAdmin
                                  ? `Change ${item.loginName} to member`
                                  : `Make ${item.loginName} an admin`
                              }
                              disabled={busy}
                              onClick={() => requestRoleChange(item)}
                              data-testid={`role-external-${item.userId}`}
                            >
                              {isAdmin ? (
                                <RemoveModeratorOutlinedIcon fontSize="small" />
                              ) : (
                                <AdminPanelSettingsOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revoke this account's access">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Revoke access for ${item.loginName}`}
                              disabled={busy}
                              onClick={() => handleRevoke(item.userId)}
                              data-testid={`revoke-external-${item.userId}`}
                            >
                              <PersonRemoveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Grant external access</DialogTitle>
        <DialogContent>
          {/* The dialog is modal: the section's alert behind it cannot be read,
              so a refusal of THIS grant is shown here, where it was asked for. */}
          {grantError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="grant-external-error"
            >
              {grantError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            Give an existing ZITADEL account access to this organization. No
            account is created and the person keeps the identity they already
            sign in with. Paste their user id — it is shown on every row of the
            members list.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="ZITADEL user id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'grant-external-user-id' } }}
          />
          <FormControl sx={{ mt: 2 }} disabled={busy}>
            <FormLabel id="grant-external-role-label">Role</FormLabel>
            <RadioGroup
              aria-labelledby="grant-external-role-label"
              name="grant-external-role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              <FormControlLabel
                value="admin"
                control={<Radio />}
                label="Admin"
                data-testid="grant-external-role-admin"
              />
              <FormControlLabel
                value="member"
                control={<Radio />}
                label="Member"
                data-testid="grant-external-role-member"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGrant}
            disabled={busy}
            data-testid="confirm-grant-external"
          >
            Grant access
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={roleTarget !== null}
        onClose={() => !busy && setRoleTarget(null)}
        fullWidth
        maxWidth="xs"
        data-testid="external-role-dialog"
      >
        <DialogTitle>{promoting ? 'Make admin' : 'Change to member'}</DialogTitle>
        <DialogContent>
          {roleError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="external-role-error"
            >
              {roleError}
            </Alert>
          )}
          {roleTarget && (
            <DialogContentText>
              {promoting ? (
                <>
                  Make <strong>{roleTarget.displayName}</strong> (
                  {roleTarget.loginName}) an <strong>admin</strong> of this
                  organization&apos;s workspace? Admins can change its settings,
                  such as MCP connections. This account belongs to another
                  organization.
                </>
              ) : (
                <>
                  Change <strong>{roleTarget.displayName}</strong> (
                  {roleTarget.loginName}) to a <strong>member</strong>? A session
                  they already have open in this organization stays admin until
                  its access token is refreshed.
                </>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSetRole}
            disabled={busy}
            data-testid="confirm-role-external"
          >
            {promoting ? 'Make admin' : 'Change to member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
